/**
 * Field Type Registry
 * 
 * Defines the semantic types of all property fields for:
 * - Type-aware field pickers (only show compatible fields)
 * - Field preview rendering (thumbnails for images, etc.)
 * - Validation of field path assignments
 */

// ============================================================================
// Field Type Definitions
// ============================================================================

/**
 * Semantic field types for property data.
 * Used to filter field pickers and render appropriate previews.
 */
export type FieldType = 
  | 'text'      // Plain text (names, titles, short descriptions)
  | 'richtext'  // HTML/markdown content (long descriptions, blurbs)
  | 'image'     // Image URLs
  | 'video'     // Video URLs
  | 'url'       // Generic URLs (websites, links)
  | 'phone'     // Phone numbers
  | 'email'     // Email addresses
  | 'number'    // Numeric values
  | 'boolean'   // True/false values
  | 'date'      // Date-only values (YYYY-MM-DD)
  | 'datetime'  // Date and time values (YYYY-MM-DDTHH:mm:ss)
  | 'json';     // JSON/structured data

/**
 * Metadata about a field including its type and display info.
 */
export interface FieldMetadata {
  /** Semantic type of the field */
  type: FieldType;
  /** Human-readable label */
  label: string;
  /** Optional description */
  description?: string;
}

// ============================================================================
// Property Scalar Field Registry
// ============================================================================

/**
 * Type information for scalar property fields.
 */
export const propertyFieldTypes: Record<string, FieldMetadata> = {
  // Identifiers
  property_name: { type: 'text', label: 'Property Name' },
  property_slug: { type: 'text', label: 'Property Slug' },
  property_type: { type: 'text', label: 'Property Type' },
  property_status: { type: 'text', label: 'Property Status' },
  
  // Branding - Logos (all images)
  property_primary_logo: { type: 'image', label: 'Primary Logo' },
  property_secondary_logo: { type: 'image', label: 'Secondary Logo' },
  property_mono_black_logo: { type: 'image', label: 'Mono Black Logo' },
  property_mono_white_logo: { type: 'image', label: 'Mono White Logo' },
  property_fav_icon: { type: 'image', label: 'Favicon' },
  property_icon: { type: 'image', label: 'Icon' },
  property_featured_image: { type: 'image', label: 'Featured Image' },
  property_featured_video: { type: 'video', label: 'Featured Video' },
  
  // Branding - Company
  property_company_name: { type: 'text', label: 'Company Name' },
  property_company_icon: { type: 'image', label: 'Company Icon' },
  property_company_logo: { type: 'image', label: 'Company Logo' },
  property_company_mono_black_logo: { type: 'image', label: 'Company Mono Black Logo' },
  property_company_mono_white_logo: { type: 'image', label: 'Company Mono White Logo' },
  property_company_url: { type: 'url', label: 'Company URL' },
  
  // Branding - Colors
  property_primary_color: { type: 'text', label: 'Primary Color' },
  property_secondary_color: { type: 'text', label: 'Secondary Color' },
  property_font_color: { type: 'text', label: 'Font Color' },
  property_color_3: { type: 'text', label: 'Color 3' },
  property_color_4: { type: 'text', label: 'Color 4' },
  property_color_5: { type: 'text', label: 'Color 5' },
  property_color_6: { type: 'text', label: 'Color 6' },
  property_color_7: { type: 'text', label: 'Color 7' },
  property_color_8: { type: 'text', label: 'Color 8' },
  
  // Contact
  property_phone: { type: 'phone', label: 'Phone' },
  property_call_tracking_phone: { type: 'phone', label: 'Call Tracking Phone' },
  property_email: { type: 'email', label: 'Email' },
  property_address: { type: 'text', label: 'Address' },
  property_address_1: { type: 'text', label: 'Address Line 1' },
  property_address_2: { type: 'text', label: 'Address Line 2' },
  property_address_city: { type: 'text', label: 'City' },
  property_address_state: { type: 'text', label: 'State' },
  property_address_zip: { type: 'text', label: 'ZIP Code' },
  property_address_zip_full: { type: 'text', label: 'Full ZIP Code' },
  property_address_country: { type: 'text', label: 'Country' },
  property_address_county: { type: 'text', label: 'County' },
  property_address_latitude: { type: 'text', label: 'Latitude' },
  property_address_longitude: { type: 'text', label: 'Longitude' },
  property_full_address: { type: 'text', label: 'Full Address' },
  property_neighborhood_address: { type: 'text', label: 'Neighborhood Address' },
  
  // URLs - Social
  property_website_url: { type: 'url', label: 'Website URL' },
  property_facebook_url: { type: 'url', label: 'Facebook URL' },
  property_instagram_url: { type: 'url', label: 'Instagram URL' },
  property_twitter_url: { type: 'url', label: 'Twitter URL' },
  property_linkedin_url: { type: 'url', label: 'LinkedIn URL' },
  property_youtube_url: { type: 'url', label: 'YouTube URL' },
  property_tiktok_url: { type: 'url', label: 'TikTok URL' },
  property_yelp_url: { type: 'url', label: 'Yelp URL' },
  property_zillow_url: { type: 'url', label: 'Zillow URL' },
  property_google_business_url: { type: 'url', label: 'Google Business URL' },
  property_google_place_url: { type: 'url', label: 'Google Place URL' },
  
  // URLs - Actions
  property_apply_now_url: { type: 'url', label: 'Apply Now URL' },
  property_resident_portal_url: { type: 'url', label: 'Resident Portal URL' },
  property_online_leasing_url: { type: 'url', label: 'Online Leasing URL' },
  property_apartments_url: { type: 'url', label: 'Apartments URL' },
  
  // Content - Descriptions
  property_short_description: { type: 'text', label: 'Short Description' },
  property_long_description: { type: 'richtext', label: 'Long Description' },
  
  // Content - Page Blurbs
  property_home_hero_title: { type: 'text', label: 'Home Hero Title' },
  property_home_romance_title: { type: 'text', label: 'Home Romance Title' },
  property_home_amenities_blurb: { type: 'richtext', label: 'Home Amenities Blurb' },
  property_home_floorplans_blurb: { type: 'richtext', label: 'Home Floorplans Blurb' },
  property_home_gallery_blurb: { type: 'richtext', label: 'Home Gallery Blurb' },
  property_home_neighborhood_blurb: { type: 'richtext', label: 'Home Neighborhood Blurb' },
  property_home_reviews_blurb: { type: 'richtext', label: 'Home Reviews Blurb' },
  property_floorplans_blurb: { type: 'richtext', label: 'Floorplans Blurb' },
  property_amenities_blurb: { type: 'richtext', label: 'Amenities Blurb' },
  property_gallery_blurb: { type: 'richtext', label: 'Gallery Blurb' },
  property_neighborhood_blurb: { type: 'richtext', label: 'Neighborhood Blurb' },
  property_virtualtours_blurb: { type: 'richtext', label: 'Virtual Tours Blurb' },
  property_contactus_blurb: { type: 'richtext', label: 'Contact Us Blurb' },
  property_reviews_blurb: { type: 'richtext', label: 'Reviews Blurb' },
  property_pets_blurb: { type: 'richtext', label: 'Pets Blurb' },
  property_faqs_blurb: { type: 'richtext', label: 'FAQs Blurb' },
  property_applynow_blurb: { type: 'richtext', label: 'Apply Now Blurb' },
  property_residentaccess_blurb: { type: 'richtext', label: 'Resident Access Blurb' },
  property_thankyou_blurb: { type: 'richtext', label: 'Thank You Blurb' },
  property_accessibility_blurb: { type: 'richtext', label: 'Accessibility Blurb' },
  property_referafriend_blurb: { type: 'richtext', label: 'Refer a Friend Blurb' },
  property_scheduletour_blurb: { type: 'richtext', label: 'Schedule Tour Blurb' },
  property_interactivemap_blurb: { type: 'richtext', label: 'Interactive Map Blurb' },
  
  // Special Offers
  property_specialoffer_title: { type: 'text', label: 'Special Offer Title' },
  property_specialoffer_description: { type: 'richtext', label: 'Special Offer Description' },
  property_specialoffer_disclaimer: { type: 'text', label: 'Special Offer Disclaimer' },

  // Property Details
  property_year_built: { type: 'text', label: 'Year Built' },
  property_year_renovated: { type: 'text', label: 'Year Renovated' },
  property_total_units: { type: 'text', label: 'Total Units' },
  property_available_units: { type: 'number', label: 'Available Units' },
  property_current_occupancy: { type: 'text', label: 'Current Occupancy' },
  
  // Unit Stats
  property_bedrooms_min: { type: 'text', label: 'Min Bedrooms' },
  property_bedrooms_max: { type: 'text', label: 'Max Bedrooms' },
  property_bedrooms_range: { type: 'text', label: 'Bedrooms Range' },
  property_bathrooms_min: { type: 'text', label: 'Min Bathrooms' },
  property_bathrooms_max: { type: 'text', label: 'Max Bathrooms' },
  property_bathrooms_range: { type: 'text', label: 'Bathrooms Range' },
  property_sqft_min: { type: 'text', label: 'Min Sq Ft' },
  property_sqft_max: { type: 'text', label: 'Max Sq Ft' },
  property_sqft_range: { type: 'text', label: 'Sq Ft Range' },
  property_price_min: { type: 'text', label: 'Min Price' },
  property_price_max: { type: 'text', label: 'Max Price' },
  property_price_range: { type: 'text', label: 'Price Range' },
  
  // Fees
  property_application_fee: { type: 'text', label: 'Application Fee' },
  property_admin_fee: { type: 'text', label: 'Admin Fee' },
  property_fees_deposit_info: { type: 'text', label: 'Fees & Deposit Info' },
  
  // Pets
  property_ispetfriendly: { type: 'boolean', label: 'Pet Friendly' },
  property_allows_cats: { type: 'boolean', label: 'Allows Cats' },
  property_allows_dogs: { type: 'boolean', label: 'Allows Dogs' },
  property_max_pets: { type: 'text', label: 'Max Pets' },
  property_max_pet_weight_limit: { type: 'text', label: 'Max Pet Weight' },
  property_pet_deposit: { type: 'text', label: 'Pet Deposit' },
  property_upfront_pet_fee: { type: 'text', label: 'Upfront Pet Fee' },
  property_monthly_pet_rent: { type: 'text', label: 'Monthly Pet Rent' },
  property_pet_restrictions: { type: 'text', label: 'Pet Restrictions' },
  property_pet_policy_description: { type: 'richtext', label: 'Pet Policy Description' },
  property_petpolicy_info: { type: 'richtext', label: 'Pet Policy Info' },
  
  // Scores
  property_walk_score: { type: 'text', label: 'Walk Score' },
  property_transit_score: { type: 'text', label: 'Transit Score' },
  
  // Other
  property_copyright: { type: 'text', label: 'Copyright' },
  property_domain: { type: 'text', label: 'Domain' },
};

// ============================================================================
// Collection Item Field Registry
// ============================================================================

/**
 * Type information for fields within collection items.
 * Uses wildcard [*] to represent any index.
 */
const feeItemFields: Record<string, FieldMetadata> = {
  name: { type: 'text', label: 'Name' },
  description: { type: 'text', label: 'Description' },
  amount: { type: 'number', label: 'Amount' },
  amount_max: { type: 'number', label: 'Max Amount' },
  amount_raw: { type: 'text', label: 'Amount (as written)' },
  amount_type: { type: 'text', label: 'Amount Type' },
  timing: { type: 'text', label: 'Timing' },
  category: { type: 'text', label: 'Category' },
  charge_code: { type: 'text', label: 'Charge Code' },
  is_required: { type: 'boolean', label: 'Required' },
  is_refundable: { type: 'boolean', label: 'Refundable' },
  is_taxable: { type: 'boolean', label: 'Taxable' },
};

const petPolicyItemFields: Record<string, FieldMetadata> = {
  pet_type: { type: 'text', label: 'Pet Type' },
  allowed: { type: 'boolean', label: 'Allowed' },
  max_count: { type: 'number', label: 'Max Count' },
  weight_limit: { type: 'number', label: 'Weight Limit' },
  deposit: { type: 'number', label: 'Deposit' },
  fee: { type: 'number', label: 'Fee' },
  monthly_rent: { type: 'number', label: 'Monthly Rent' },
  description: { type: 'text', label: 'Description' },
  restrictions: { type: 'text', label: 'Restrictions' },
};

const virtualTourItemFields: Record<string, FieldMetadata> = {
  virtual_tour_thumbnail: { type: 'image', label: 'Thumbnail' },
  virtual_tour_title: { type: 'text', label: 'Title' },
  virtual_tour_url: { type: 'url', label: 'URL' },
  virtual_tour_type: { type: 'text', label: 'Type' },
  virtual_tour_description: { type: 'text', label: 'Description' },
  url_health_error: { type: 'text', label: 'URL Health Error' },
};

/** Prefix each item field so the same shape can hang off the property, a floorplan, or a unit. */
function at(prefix: string, fields: Record<string, FieldMetadata>): Record<string, FieldMetadata> {
  return Object.fromEntries(Object.entries(fields).map(([key, meta]) => [`${prefix}.${key}`, meta]));
}

export const collectionFieldTypes: Record<string, FieldMetadata> = {
  // Photo Gallery
  'photo_gallery[*].image': { type: 'image', label: 'Image' },
  'photo_gallery[*].original_url': { type: 'url', label: 'Original URL' },
  'photo_gallery[*].title': { type: 'text', label: 'Title' },
  'photo_gallery[*].description': { type: 'text', label: 'Description' },
  'photo_gallery[*].alt_text': { type: 'text', label: 'Alt Text' },
  'photo_gallery[*].link': { type: 'url', label: 'Link' },
  'photo_gallery[*].link_text': { type: 'text', label: 'Link Text' },
  
  // Amenities
  'amenities[*].title': { type: 'text', label: 'Title' },
  'amenities[*].description': { type: 'richtext', label: 'Description' },
  'amenities[*].group': { type: 'text', label: 'Group' },
  'amenities[*].amenity_icon': { type: 'text', label: 'Icon' },
  'amenities[*].amenity_icon_alt_text': { type: 'text', label: 'Icon Alt Text' },
  'amenities[*].amenity_image': { type: 'image', label: 'Image' },
  'amenities[*].amenity_image_alt_text': { type: 'text', label: 'Image Alt Text' },
  'amenities[*].amount': { type: 'text', label: 'Amount' },
  'amenities[*].is_featured': { type: 'text', label: 'Is Featured' },
  
  // Floorplans
  'floorplans[*].floorplan_id': { type: 'text', label: 'Floorplan ID' },
  'floorplans[*].floorplan_name': { type: 'text', label: 'Name' },
  'floorplans[*].floorplan_image_url': { type: 'image', label: 'Image' },
  'floorplans[*].floorplan_beds_min': { type: 'text', label: 'Min Beds' },
  'floorplans[*].floorplan_beds_max': { type: 'text', label: 'Max Beds' },
  'floorplans[*].floorplan_baths_min': { type: 'text', label: 'Min Baths' },
  'floorplans[*].floorplan_baths_max': { type: 'text', label: 'Max Baths' },
  'floorplans[*].floorplan_sqft_min': { type: 'text', label: 'Min Sq Ft' },
  'floorplans[*].floorplan_sqft_max': { type: 'text', label: 'Max Sq Ft' },
  'floorplans[*].floorplan_price_min': { type: 'text', label: 'Min Price' },
  'floorplans[*].floorplan_price_max': { type: 'text', label: 'Max Price' },
  'floorplans[*].floorplan_available_units': { type: 'text', label: 'Available Units' },
  'floorplans[*].floorplan_code': { type: 'text', label: 'Code' },
  'floorplans[*].floorplan_description': { type: 'text', label: 'Description' },
  'floorplans[*].floorplan_unit_count': { type: 'number', label: 'Unit Count' },
  'floorplans[*].floorplan_deposit_min': { type: 'number', label: 'Min Deposit' },
  'floorplans[*].floorplan_deposit_max': { type: 'number', label: 'Max Deposit' },
  'floorplans[*].floorplan_max_occupants': { type: 'number', label: 'Max Occupants' },
  'floorplans[*].floorplan_is_disabled': { type: 'boolean', label: 'Disabled' },
  // Units hang off each floorplan. They are not a top-level collection.
  'floorplans[*].units[*].unit_id': { type: 'text', label: 'Unit ID' },
  'floorplans[*].units[*].unit_name': { type: 'text', label: 'Unit Name' },
  'floorplans[*].units[*].floorplan_id': { type: 'text', label: 'Floorplan ID' },
  'floorplans[*].units[*].floorplan_name': { type: 'text', label: 'Floorplan Name' },
  'floorplans[*].units[*].bedrooms': { type: 'number', label: 'Bedrooms' },
  'floorplans[*].units[*].bathrooms': { type: 'number', label: 'Bathrooms' },
  'floorplans[*].units[*].sqft': { type: 'number', label: 'Sq Ft' },
  'floorplans[*].units[*].available': { type: 'boolean', label: 'Available' },
  'floorplans[*].units[*].available_date': { type: 'date', label: 'Available Date' },
  'floorplans[*].units[*].status': { type: 'text', label: 'Status' },
  'floorplans[*].units[*].price_min': { type: 'number', label: 'Min Price' },
  'floorplans[*].units[*].price_max': { type: 'number', label: 'Max Price' },
  'floorplans[*].units[*].rent_market': { type: 'number', label: 'Market Rent' },
  'floorplans[*].units[*].deposit_min': { type: 'number', label: 'Min Deposit' },
  'floorplans[*].units[*].deposit_max': { type: 'number', label: 'Max Deposit' },
  'floorplans[*].units[*].is_furnished': { type: 'boolean', label: 'Furnished' },
  'floorplans[*].units[*].floor': { type: 'text', label: 'Floor' },
  'floorplans[*].units[*].building': { type: 'text', label: 'Building' },
  'floorplans[*].units[*].make_ready_date': { type: 'date', label: 'Make Ready Date' },
  'floorplans[*].units[*].max_occupants': { type: 'number', label: 'Max Occupants' },
  'floorplans[*].units[*].apply_url': { type: 'url', label: 'Apply URL' },
  
  // Floorplan Groups
  'floorplan_groups[*].floorplan_group_id': { type: 'text', label: 'Group ID' },
  'floorplan_groups[*].floorplan_group_name': { type: 'text', label: 'Group Name' },
  'floorplan_groups[*].floorplan_group_url': { type: 'url', label: 'Group URL' },
  'floorplan_groups[*].floorplan_group_image_url': { type: 'image', label: 'Group Image' },
  'floorplan_groups[*].floorplan_group_image_alt': { type: 'text', label: 'Group Image Alt' },
  
  // Virtual Tours
  ...at('virtual_tours[*]', virtualTourItemFields),
  // The same tour shape hung off a unit.
  ...at('floorplans[*].units[*].virtual_tours[*]', virtualTourItemFields),
  
  // Office Hours
  'office_hours[*].day': { type: 'text', label: 'Day' },
  'office_hours[*].open_time': { type: 'text', label: 'Open Time' },
  'office_hours[*].close_time': { type: 'text', label: 'Close Time' },
  
  // Neighborhood POIs
  'neighborhood_pois[*].poi_name': { type: 'text', label: 'Name' },
  'neighborhood_pois[*].poi_type': { type: 'text', label: 'Type' },
  'neighborhood_pois[*].poi_address': { type: 'text', label: 'Address' },
  'neighborhood_pois[*].poi_latitude': { type: 'text', label: 'Latitude' },
  'neighborhood_pois[*].poi_longitude': { type: 'text', label: 'Longitude' },
  'neighborhood_pois[*].poi_short_description': { type: 'text', label: 'Short Description' },
  'neighborhood_pois[*].poi_long_description': { type: 'richtext', label: 'Long Description' },
  'neighborhood_pois[*].poi_featured_image': { type: 'image', label: 'Featured Image' },
  'neighborhood_pois[*].poi_business_url': { type: 'url', label: 'Business URL' },
  'neighborhood_pois[*].poi_business_phone': { type: 'phone', label: 'Business Phone' },
  'neighborhood_pois[*].poi_miles_from_property': { type: 'text', label: 'Miles from Property' },
  'neighborhood_pois[*].poi_walking_minutes': { type: 'text', label: 'Walking Minutes' },
  'neighborhood_pois[*].poi_driving_minutes': { type: 'text', label: 'Driving Minutes' },
  'neighborhood_pois[*].poi_biking_minutes': { type: 'text', label: 'Biking Minutes' },
  
  // Reviews
  'reviews[*].name': { type: 'text', label: 'Reviewer Name' },
  'reviews[*].title': { type: 'text', label: 'Title' },
  'reviews[*].text': { type: 'richtext', label: 'Review Text' },
  'reviews[*].rating': { type: 'text', label: 'Rating' },
  'reviews[*].date': { type: 'date', label: 'Date' },
  
  // Special Offers
  'special_offers[*].title': { type: 'text', label: 'Title' },
  'special_offers[*].description': { type: 'richtext', label: 'Description' },
  'special_offers[*].disclaimer': { type: 'text', label: 'Disclaimer' },
  'special_offers[*].start_date': { type: 'datetime', label: 'Start Date' },
  'special_offers[*].end_date': { type: 'datetime', label: 'End Date' },
  'special_offers[*].enabled': { type: 'text', label: 'Enabled' },
  'special_offers[*].lease_term': { type: 'text', label: 'Lease Term' },

  // FAQs
  'faqs[*].question': { type: 'text', label: 'Question' },
  'faqs[*].answer': { type: 'richtext', label: 'Answer' },
  'faqs[*].category': { type: 'text', label: 'Category' },

  // Fees: property-wide, and the same shape under a floorplan or a unit.
  ...at('fees[*]', feeItemFields),
  ...at('floorplans[*].fees[*]', feeItemFields),
  ...at('floorplans[*].units[*].fees[*]', feeItemFields),

  // Pet policies: property-wide, and per floorplan.
  ...at('pet_policies[*]', petPolicyItemFields),
  ...at('floorplans[*].pet_policies[*]', petPolicyItemFields),
};

// ============================================================================
// Collection Names and Their Item Types
// ============================================================================

/**
 * Available collection names on the Property object.
 */
export type CollectionName = 
  | 'photo_gallery'
  | 'amenities'
  | 'floorplans'
  | 'floorplan_groups'
  | 'virtual_tours'
  | 'office_hours'
  | 'neighborhood_pois'
  | 'reviews'
  | 'special_offers'
  | 'faqs'
  | 'fees'
  | 'pet_policies';

/**
 * Metadata about each collection.
 */
export interface CollectionMetadata {
  /** Human-readable label */
  label: string;
  /** Singular label for individual items */
  singularLabel: string;
  /** Description */
  description?: string;
  /** Primary display field for item preview */
  primaryField: string;
  /** Secondary display field */
  secondaryField?: string;
  /** Image field for thumbnail preview (if applicable) */
  imageField?: string;
}

/**
 * Collection metadata registry.
 */
export const collectionMetadata: Record<CollectionName, CollectionMetadata> = {
  photo_gallery: {
    label: 'Photo Gallery',
    singularLabel: 'Photo',
    primaryField: 'title',
    secondaryField: 'description',
    imageField: 'image',
  },
  amenities: {
    label: 'Amenities',
    singularLabel: 'Amenity',
    primaryField: 'title',
    secondaryField: 'description',
    imageField: 'amenity_image',
  },
  floorplans: {
    label: 'Floor Plans',
    singularLabel: 'Floor Plan',
    primaryField: 'floorplan_name',
    secondaryField: 'floorplan_price_min',
    imageField: 'floorplan_image_url',
  },
  floorplan_groups: {
    label: 'Floor Plan Groups',
    singularLabel: 'Floor Plan Group',
    primaryField: 'floorplan_group_name',
    imageField: 'floorplan_group_image_url',
  },
  virtual_tours: {
    label: 'Virtual Tours',
    singularLabel: 'Virtual Tour',
    primaryField: 'virtual_tour_title',
    secondaryField: 'virtual_tour_url',
    imageField: 'virtual_tour_thumbnail',
  },
  office_hours: {
    label: 'Office Hours',
    singularLabel: 'Office Hours',
    primaryField: 'day',
    secondaryField: 'open_time',
  },
  neighborhood_pois: {
    label: 'Neighborhood POIs',
    singularLabel: 'POI',
    primaryField: 'poi_name',
    secondaryField: 'poi_type',
    imageField: 'poi_featured_image',
  },
  reviews: {
    label: 'Reviews',
    singularLabel: 'Review',
    primaryField: 'name',
    secondaryField: 'rating',
  },
  special_offers: {
    label: 'Special Offers',
    singularLabel: 'Special Offer',
    primaryField: 'title',
    secondaryField: 'description',
  },
  faqs: {
    label: 'FAQs',
    singularLabel: 'FAQ',
    primaryField: 'question',
    secondaryField: 'answer',
  },
  fees: {
    label: 'Fees',
    singularLabel: 'Fee',
    primaryField: 'name',
    secondaryField: 'amount_raw',
  },
  pet_policies: {
    label: 'Pet Policies',
    singularLabel: 'Pet Policy',
    primaryField: 'pet_type',
    secondaryField: 'description',
  },
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get the field type for a given field path.
 * Supports both scalar property fields and collection item fields.
 * 
 * @example
 * getFieldType('property_name') // { type: 'text', label: 'Property Name' }
 * getFieldType('photo_gallery[0].image') // { type: 'image', label: 'Image' }
 * getFieldType('amenities[5].title') // { type: 'text', label: 'Title' }
 */
export function getFieldType(fieldPath: string): FieldMetadata | null {
  // Check scalar property fields first
  if (propertyFieldTypes[fieldPath]) {
    return propertyFieldTypes[fieldPath];
  }
  
  // Convert indexed path to wildcard pattern: photo_gallery[3].image → photo_gallery[*].image
  const wildcardPath = fieldPath.replace(/\[\d+\]/g, '[*]');
  
  if (collectionFieldTypes[wildcardPath]) {
    return collectionFieldTypes[wildcardPath];
  }

  // Media snapshot subfields: every image field has a paired `<field>_media`
  // (e.g. property_featured_image_media.alt_text). Resolve these from the base
  // image field so alt mappings validate against the registry.
  const mediaMeta = getMediaSubfieldType(fieldPath);
  if (mediaMeta) return mediaMeta;
  
  // Fallback: custom fields (prefixed with "custom_")
  // These are dynamically flattened onto the property at load time,
  // so they won't be in the static registry. Provide a sensible default.
  if (fieldPath.startsWith('custom_')) {
    const label = formatCustomFieldLabel(fieldPath);
    // Multihub URL-style custom fields use an `_url` segment (e.g. custom_tour_url).
    if (fieldPath.includes('_url')) {
      return { type: 'url', label };
    }
    return { type: 'text', label };
  }
  
  return null;
}

/**
 * Check if a field path matches a specific field type.
 */
export function fieldMatchesType(fieldPath: string, type: FieldType): boolean {
  const metadata = getFieldType(fieldPath);
  return metadata?.type === type;
}

/**
 * Get all field paths that match a given type.
 * For collection fields, returns the wildcard pattern (caller must expand indices).
 */
export function getFieldPathsOfType(type: FieldType): string[] {
  const paths: string[] = [];
  
  for (const [path, meta] of Object.entries(propertyFieldTypes)) {
    if (meta.type === type) {
      paths.push(path);
    }
  }
  
  for (const [path, meta] of Object.entries(collectionFieldTypes)) {
    if (meta.type === type) {
      paths.push(path);
    }
  }
  
  return paths;
}

/**
 * Parse a field path into its components.
 * 
 * @example
 * parseFieldPath('property_name') 
 * // { collection: null, index: null, field: 'property_name' }
 * 
 * parseFieldPath('photo_gallery[2].image')
 * // { collection: 'photo_gallery', index: 2, field: 'image' }
 */
export function parseFieldPath(fieldPath: string): {
  collection: CollectionName | null;
  index: number | null;
  field: string;
} {
  // Match pattern: collection[index].field
  const match = fieldPath.match(/^([a-z_]+)\[(\d+)\]\.(.+)$/);
  
  if (match) {
    return {
      collection: match[1] as CollectionName,
      index: parseInt(match[2], 10),
      field: match[3],
    };
  }
  
  // Simple field
  return {
    collection: null,
    index: null,
    field: fieldPath,
  };
}

/**
 * Build a field path from components.
 * 
 * @example
 * buildFieldPath({ collection: 'photo_gallery', index: 2, field: 'image' })
 * // 'photo_gallery[2].image'
 */
export function buildFieldPath(parts: {
  collection: CollectionName | null;
  index: number | null;
  field: string;
}): string {
  if (parts.collection !== null && parts.index !== null) {
    return `${parts.collection}[${parts.index}].${parts.field}`;
  }
  return parts.field;
}

/**
 * Get human-readable label for a field path.
 * 
 * @example
 * getFieldLabel('property_name') // 'Property Name'
 * getFieldLabel('photo_gallery[0].image') // 'Photo Gallery #1 - Image'
 */
export function getFieldLabel(fieldPath: string): string {
  const parsed = parseFieldPath(fieldPath);
  
  if (parsed.collection) {
    const collectionMeta = collectionMetadata[parsed.collection];
    const fieldMeta = getFieldType(fieldPath);
    const collectionLabel = collectionMeta?.singularLabel || parsed.collection;
    const fieldLabel = fieldMeta?.label || parsed.field;
    return `${collectionLabel} #${(parsed.index ?? 0) + 1} - ${fieldLabel}`;
  }
  
  const meta = getFieldType(fieldPath);
  return meta?.label || formatFieldName(fieldPath);
}

// ============================================================================
// Media Field Helpers
// ============================================================================

/**
 * Every image/video field has a paired `<field>_media` snapshot in GraphQL. Given a
 * media field path (scalar or collection-indexed), return its media path.
 *
 * @example
 * mediaFieldPath('property_featured_image')  // 'property_featured_image_media'
 * mediaFieldPath('photo_gallery[3].image')   // 'photo_gallery[3].image_media'
 */
export function mediaFieldPath(imagePath: string): string {
  return `${imagePath}_media`;
}

/**
 * Alt-text path for an image field, resolved from its media snapshot.
 *
 * @example
 * imageAltFieldPath('property_featured_image') // 'property_featured_image_media.alt_text'
 */
export function imageAltFieldPath(imagePath: string): string {
  return `${mediaFieldPath(imagePath)}.alt_text`;
}

/** Semantic types for media snapshot subfields (image + video). */
const MEDIA_SUBFIELD_TYPES: Record<string, FieldMetadata> = {
  alt_text: { type: 'text', label: 'Alt Text' },
  public_id: { type: 'text', label: 'Public ID' },
  storage_key: { type: 'text', label: 'Storage Key' },
  classification: { type: 'text', label: 'Classification' },
  width: { type: 'number', label: 'Width' },
  height: { type: 'number', label: 'Height' },
  thumbnail_key: { type: 'text', label: 'Thumbnail Key' },
  title: { type: 'text', label: 'Title' },
  description: { type: 'text', label: 'Description' },
};

/**
 * Resolve a `<mediaField>_media.<subfield>` path (e.g.
 * `property_featured_image_media.alt_text`) by validating the base field is an
 * image or video and mapping the snapshot subfield. Returns null for non-media paths.
 */
function getMediaSubfieldType(fieldPath: string): FieldMetadata | null {
  const match = fieldPath.match(/^(.+)_media\.([a-z_]+)$/);
  if (!match) return null;

  const [, basePath, subfield] = match;
  const subMeta = MEDIA_SUBFIELD_TYPES[subfield];
  if (!subMeta) return null;

  const baseType = getFieldType(basePath)?.type;
  if (baseType !== 'image' && baseType !== 'video') return null;

  const baseLabel = getFieldType(basePath)?.label;
  return baseLabel ? { ...subMeta, label: `${baseLabel} ${subMeta.label}` } : subMeta;
}

/**
 * Format a raw field name to be human-readable.
 * Fallback when no metadata is available.
 */
function formatFieldName(field: string): string {
  return field
    .replace(/^property_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ============================================================================
// Custom Field Utilities
// ============================================================================

/**
 * Format a custom field ID into a human-readable label.
 * Strips the "custom_" prefix and converts to Title Case.
 * 
 * @example
 * formatCustomFieldLabel('custom_eliseai_tour_scheduler_url')
 * // → "Eliseai Tour Scheduler Url"
 */
export function formatCustomFieldLabel(id: string): string {
  return id
    .replace(/^custom_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Infer a FieldType from a Multihub custom field type string.
 * Custom fields declare their type (e.g., "STRING", "URL") in the GraphQL response.
 * 
 * @example
 * inferCustomFieldType('STRING') // → 'text'
 * inferCustomFieldType('URL')    // → 'url'
 */
export function inferCustomFieldType(cfType: string): FieldType {
  switch (cfType?.toUpperCase()) {
    case 'URL': return 'url';
    case 'IMAGE': return 'image';
    case 'VIDEO': return 'video';
    case 'EMAIL': return 'email';
    case 'PHONE': return 'phone';
    case 'NUMBER': case 'INTEGER': case 'FLOAT': return 'number';
    case 'BOOLEAN': return 'boolean';
    case 'RICHTEXT': case 'HTML': return 'richtext';
    case 'DATE': return 'date';
    case 'DATETIME': return 'datetime';
    case 'JSON': return 'json';
    case 'STRING':
    default:
      return 'text';
  }
}
